import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { connectInfiniteHits } from 'react-instantsearch-dom';

import Hit from './product-card';
import Button from '../../atoms/button';

import { ProductProvider } from '../../utils/context/product-provider';

export const ProductGrid = styled.div(({ theme }) => ({
  display: 'grid',
  gridGap: '1.5rem',
  width: '100%',
  gridTemplateColumns: 'repeat(2, 1fr)',
  [`@media screen and (min-width: ${theme.breakpoint.medium})`]: {
    gridTemplateColumns: 'repeat(3, 1fr)'
  }
}));

const GridButton = styled(Button)(({ theme }) => ({
  gridColumn: '-1/1',
  whiteSpace: 'nowrap',
  [`@media screen and (min-width: ${theme.breakpoint.medium})`]: {
    width: 'fit-content',
    margin: '0 auto'
  }
}));

const GridSentinel = styled.span(() => ({
  gridColumn: '-1/1'
}));

const Loading = styled.span(({ theme }) => ({
  display: 'block',
  gridArea: 'loader',
  height: '6rem',
  position: 'relative',
  width: '100%',
  '::after': {
    animation: 'spin .5s infinite linear',
    border: `0.25rem solid ${theme.color.brand}`,
    borderRadius: 290486,
    borderRightColor: 'transparent',
    borderTopColor: 'transparent',
    content: '""',
    height: '3rem',
    left: 'calc(50% - (3rem / 2))',
    position: 'absolute',
    top: 'calc(50% - (3rem / 2))',
    width: '3rem'
  }
}));

class InfiniteHits extends React.Component {
  sentinel = null;

  constructor(props) {
    super(props);
    this.state = {
      activeQuickShopID: -1,
      viewNext: false
    };
  }

  componentDidMount() {
    this.observer = new global.IntersectionObserver(this.onSentinelIntersection);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.viewNext && prevState.viewNext !== this.state.viewNext) {
      this.observer.observe(this.sentinel);
    }
  }

  componentWillUnmount = () => {
    this.observer.disconnect();
  }

  onSentinelIntersection = (entries) => {
    const { hasMore, refineNext } = this.props;

    entries.forEach((entry) => {
      if (entry.isIntersecting && hasMore) {
        refineNext();
      }
    });
  };

  setActiveQuickshopID = (id) => this.setState({ activeQuickShopID: id })

  render() {
    const { hits } = this.props;

    return (
      <ProductProvider>
        <ProductGrid>
          {hits.map((hit) => (
            <Hit
              key={hit.objectID}
              hit={hit}
              activeQuickShopID={this.state.activeQuickShopID}
              setActiveQuickshopID={this.setActiveQuickshopID}
            />
          ))}

          <GridSentinel
            // eslint-disable-next-line no-return-assign
            ref={(c) => (this.sentinel = c)}
          >
            { this.state.viewNext && this.props.hasMore && <Loading /> }
          </GridSentinel>

          {
            this.props.hasMore && !this.state.viewNext && (
              <GridButton
                type="button"
                onClick={() => this.setState({ viewNext: true })}
              >
                Click to Load More Results
              </GridButton>
            )
          }
        </ProductGrid>
      </ProductProvider>
    );
  }
}

InfiniteHits.propTypes = {
  hits: PropTypes.arrayOf(PropTypes.object).isRequired,
  hasMore: PropTypes.bool.isRequired,
  refineNext: PropTypes.func.isRequired
};

export default connectInfiniteHits(InfiniteHits);
